import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Detail = { title: string; body: string };
type Service = { number: string; title: string; calligraphy: string; details: Detail[] };

type FlatItem = {
  serviceTitle: string;
  serviceCalligraphy: string;
  serviceNumber: string;
  detail: Detail;
  globalIndex: number;
  total: number;
};

type Props = {
  globalIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
};

export default function ServiceDetailPanel({ globalIndex, onClose, onNavigate }: Props) {
  const { t } = useTranslation();
  const services = t("servicesPreview.services", { returnObjects: true }) as Service[];

  const flatItems: FlatItem[] = Array.isArray(services)
    ? services.flatMap((s) =>
        Array.isArray(s.details)
          ? s.details.map((d) => ({
              serviceTitle: s.title,
              serviceCalligraphy: s.calligraphy,
              serviceNumber: s.number,
              detail: d,
              globalIndex: 0,
              total: 0,
            }))
          : []
      ).map((item, i, arr) => ({ ...item, globalIndex: i, total: arr.length }))
    : [];

  const isOpen = globalIndex !== null;
  const current = globalIndex !== null ? flatItems[globalIndex] : null;
  const hasPrev = globalIndex !== null && globalIndex > 0;
  const hasNext = globalIndex !== null && globalIndex < flatItems.length - 1;

  // Map service number to query param
  const serviceParam = current?.serviceNumber ?? "01";

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && hasNext && globalIndex !== null) onNavigate(globalIndex + 1);
      if (e.key === "ArrowLeft" && hasPrev && globalIndex !== null) onNavigate(globalIndex - 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, hasNext, hasPrev, globalIndex, onClose, onNavigate]);

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-[#1E202A]/50 transition-opacity duration-500 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[540px] bg-[#EDEEF3] flex flex-col transition-transform duration-500 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-10 py-7 border-b border-[#D0D1DB]">
          <div className="flex items-center gap-4">
            {current && (
              <span
                className="text-[#8A7A6A]/40 select-none"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "13px", fontStyle: "italic" }}
              >
                {current.serviceNumber} · {current.serviceTitle}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-[#74758A] hover:text-[#1E202A] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-10 py-10">
          {current && (
            <div
              key={globalIndex}
              className="animate-in fade-in duration-300"
              style={{ animation: "fadeIn 0.3s ease" }}
            >
              <span
                className="text-[#8A7A6A]/50 block leading-none mb-6"
                style={{ fontFamily: "'Great Vibes', cursive", fontSize: "24px" }}
              >
                {current.serviceCalligraphy}
              </span>

              <h2
                className="text-[#1E202A] leading-tight mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(24px, 2.8vw, 32px)", fontWeight: 400 }}
              >
                {current.detail.title}
              </h2>

              <div className="w-10 h-px bg-[#8A7A6A]/30 mb-8" />

              <p className="text-[#74758A] text-sm leading-[2.2]">
                {current.detail.body}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-10 py-6 border-t border-[#D0D1DB] bg-[#E4E5EC]">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => hasPrev && globalIndex !== null && onNavigate(globalIndex - 1)}
              disabled={!hasPrev}
              className={`flex items-center gap-2 text-sm transition-all duration-200 cursor-pointer ${hasPrev ? "text-[#1E202A] hover:text-[#8A7A6A]" : "text-[#C0C1CB] cursor-not-allowed"}`}
            >
              <i className="ri-arrow-left-line text-sm" />
              <span style={{ letterSpacing: "0.02em" }}>Prev</span>
            </button>

            <span className="text-[#A8AABB] text-xs tracking-widest">
              {globalIndex !== null ? globalIndex + 1 : "—"} / {flatItems.length}
            </span>

            <button
              onClick={() => hasNext && globalIndex !== null && onNavigate(globalIndex + 1)}
              disabled={!hasNext}
              className={`flex items-center gap-2 text-sm transition-all duration-200 cursor-pointer ${hasNext ? "text-[#1E202A] hover:text-[#8A7A6A]" : "text-[#C0C1CB] cursor-not-allowed"}`}
            >
              <span style={{ letterSpacing: "0.02em" }}>Next</span>
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </div>

          {/* CTA — passes service number as query param so Contact form pre-selects */}
          <Link
            to={`/contact?service=${serviceParam}`}
            onClick={onClose}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1E202A] text-[#EDEEF3] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ letterSpacing: "0.03em" }}
          >
            {t("servicesPreview.consultationCta")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </>
  );
}
